import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  input,
  output,
  signal,
  ViewChild,
} from '@angular/core';
import { TableColumn } from '../../atoms/table/table.model';
import { TableComponent } from '../../atoms/table/table.component';
import { CenterIndicatorComponent } from '../../molecules/indicators/center-indicator.component';
import { OutputsBtIndicatorComponent } from '../../molecules/indicators/outputs-bt-indicator.component';
import { IntensityIndicatorComponent } from '../../molecules/indicators/intensity-indicator.component';
import { PowerIndicatorComponent } from '../../molecules/indicators/power-indicator.component';
import { WaveQualityIndicatorComponent } from '../../molecules/indicators/wave-quality-indicator.component';
import { TemperatureIndicatorComponent } from '../../molecules/indicators/temperature-indicator.component';
import { StatusUpdateIndicatorComponent } from '../../molecules/indicators/status-update-indicator.component';
import { RsabtIndicatorComponent } from '../../molecules/indicators/rsabt-indicator.component';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { NzTableQueryParams } from 'ng-zorro-antd/table';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslocoDecimalPipe } from '@jsverse/transloco-locale';
import { DISTRIBUTION_NETWORK_TABLE_DEFAULT_COLUMNS } from '../../../constants/table.constants';
import { IconComponent } from '../../atoms/icon/icon.component';
import { TooltipDirective } from '@ui';
import { PermissionsService } from '../../../../app/core/services/permissions.service';

@Component({
  selector: 'distribution-network-table',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TableComponent,
    RsabtIndicatorComponent,
    CenterIndicatorComponent,
    OutputsBtIndicatorComponent,
    IntensityIndicatorComponent,
    PowerIndicatorComponent,
    WaveQualityIndicatorComponent,
    TemperatureIndicatorComponent,
    StatusUpdateIndicatorComponent,
    TranslocoDirective,
    TranslocoDecimalPipe,
    IconComponent,
    TooltipDirective
  ],
  templateUrl: 'distribution-network-table.component.html',
})
export class DistributionNetworkTableComponent {
  private translocoService = inject(TranslocoService);
  private destroyRef = inject(DestroyRef);
  protected readonly permissionsService = inject(PermissionsService);

  data = input<any[]>([]);

  // computed that guarantees a stable id per row (uses the backend id or generates a stable key)
  dataWithId = computed(() =>
    this.data().map((item) => {
      const stableId =  `${item.centerId ?? ''}_${item.rsabtId ?? ''}_${new Date(item.updatedAt ?? item.date ?? Date.now()).getTime()}`;
      item.id = stableId;
      return item;
    }),
  );

  total = input<number>(100);
  loading = input<boolean>(false);
  pageSize = input<number>(10);
  pageIndex = input<number>(1);
  showPagination = input<boolean>(true);
  showPageSizeChanger = input<boolean>(true);
  showCenterCode = input<boolean>(false);

  onQueryParams = output<{
    orderBy: string | undefined;
    order: string | undefined;
    pageSize: number;
    pageIndex: number;
  }>();
  onCenter = output<{
    distributorId: string;
    centerId: string;
    rsabtId: string;
    vtnId: string;
  }>();
  onCopyCenterId = output<string>();
  onRsabtId = output<string>();
  onSettings = output<any>();

  @ViewChild('transformerTemplate') transformerTemplate!: ElementRef;
  @ViewChild('centerTemplate') centerTemplate!: ElementRef;
  @ViewChild('outputsBtTemplate') outputsBtTemplate!: ElementRef;
  @ViewChild('intensityTemplate') intensityTemplate!: ElementRef;
  @ViewChild('powerTemplate') powerTemplate!: ElementRef;
  @ViewChild('waveQualityTemplate') waveQualityTemplate!: ElementRef;
  @ViewChild('temperatureTemplate') temperatureTemplate!: ElementRef;
  @ViewChild('statusUpdateTemplate') statusUpdateTemplate!: ElementRef;
  @ViewChild('actionsTemplate') actionsTemplate!: ElementRef;

  columnTemplates: any = {};

  columns = signal<TableColumn[]>([]);
  scrollConfigDefault = signal<{ x?: string | null; y?: string | null }>({
    x: '1000px',
    y: 'calc(100vh - 350px)',
  });
  scrollConfig = computed<{ x?: string | null; y?: string | null }>(() =>
    this.data()?.length ? this.scrollConfigDefault() : {},
  );
  DEFAULT_COLUMNS = computed<TableColumn[]>(() => {
    let cols = DISTRIBUTION_NETWORK_TABLE_DEFAULT_COLUMNS;
    // If the user does not have read permissions, remove 'actions'
    if (!this.hasReadPermission()) {
      cols = cols.filter(col => col.key !== 'actions');
    }
    return cols;
  });
  permissions = this.permissionsService.permissions;

  constructor() {
    
    effect(() => {
      this.setLocalizeColumns();
    });
  }

  /**
   * Returns true if the user has 'w' write permission.
   */
  hasWritePermission(): boolean {
    return this.permissions().some(p => p === 'sabt:alarms:w');
  }

  /**
   * Returns true if the user has 'r' read permission.
   */
  hasReadPermission(): boolean {
    return this.permissions().some(p => p === 'sabt:ltbox:r');
  }

  ngAfterViewInit(): void {
    this.setColumnTemplates();
  }

  setLocalizeColumns() {
    this.translocoService
      .selectTranslation()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.columns.set(
          this.DEFAULT_COLUMNS().map((col) => ({
            ...col,
            title: this.translocoService.translate(col.title),
          })),
        );
      });
  }

  setColumnTemplates() {
    this.columnTemplates = {
      transformer: this.transformerTemplate,
      center: this.centerTemplate,
      outputBt: this.outputsBtTemplate,
      intensity: this.intensityTemplate,
      power: this.powerTemplate,
      waveQuality: this.waveQualityTemplate,
      temperature: this.temperatureTemplate,
      statusUpdate: this.statusUpdateTemplate,
      actions: this.actionsTemplate,
    };
  }

  emitRsabtId(id: string) {
    this.onRsabtId.emit(id);
  }

  onQueryParamsChange(params: NzTableQueryParams): void {
    const { sort, pageSize, pageIndex } = params;
    const currentSort = sort.find((item) => item.value !== null);
    const orderBy = (currentSort && currentSort.key) || undefined;
    const order = (currentSort && currentSort.value) || undefined;
    this.onQueryParams.emit({ orderBy, order, pageSize, pageIndex });
  }

  emitCenter(event: {
    distributorId: string;
    centerId: string;
    rsabtId: string;
    vtnId: string
  }) {
    this.onCenter.emit(event);
  }

  onSettingsClick(row: any) {
    this.onSettings.emit(row);
  }
}
